import React from 'react';
import { Box, Typography } from '@mui/material';
import { PlayArrow } from '@mui/icons-material';
import Button from './Button';
import ManualSaveNotice, { SAVE_NEEDS_ACCOUNT_MESSAGE } from '../ManualSaveNotice';
import { COLOR } from './tokens';

export const MANUAL_END_NOTE = 'You ended this run early, so it was not saved. Finish a run to post your score.';
export const GUEST_NOTICE = { message: SAVE_NEEDS_ACCOUNT_MESSAGE, needsAccount: true };

/**
 * End-of-run panel: title, final score, optional details and the save /
 * register notice. Used for natural endings and for a manual End / New Game,
 * so a player always sees how the run went before starting another one.
 */
export default function ResultView({
    title = 'Game Over',
    score,
    scoreLabel = 'Score',
    details = null,
    endedManually = false,
    isGuest = false,
    notice = null,
    accentColor = COLOR.primary,
    onPlayAgain,
    playAgainLabel = 'Play Again',
    autoFocus = true,
}) {
    const shownNotice = notice || (isGuest ? GUEST_NOTICE : null);
    return (
        <Box
            data-testid="result-view"
            role="dialog"
            aria-label={title}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                textAlign: 'center',
                maxWidth: 420,
                zIndex: 1,
            }}
        >
            <Typography variant="h5" component="h2" sx={{ color: COLOR.text, fontWeight: 800 }}>
                {title}
            </Typography>
            {score != null && score !== '' ? (
                <Box>
                    <Typography sx={{ color: COLOR.textMuted, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 1 }}>
                        {scoreLabel}
                    </Typography>
                    <Typography
                        data-testid="result-score"
                        className="evx-tabular"
                        sx={{ color: accentColor, fontSize: '2.5rem', fontWeight: 800, lineHeight: 1.1 }}
                    >
                        {score}
                    </Typography>
                </Box>
            ) : null}
            {details}
            {endedManually ? (
                <Typography data-testid="result-manual-note" variant="body2" sx={{ color: COLOR.textMuted }}>
                    {MANUAL_END_NOTE}
                </Typography>
            ) : null}
            <ManualSaveNotice notice={shownNotice} color={COLOR.text} />
            {onPlayAgain ? (
                <Button onClick={onPlayAgain} startIcon={<PlayArrow />} autoFocus={autoFocus} sx={{ mt: 1 }}>
                    {playAgainLabel}
                </Button>
            ) : null}
        </Box>
    );
}
